
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import OverPriceSms from "@/components/OverPriceSms.vue";
import UserListAvatar from "@/components/users/UserListAvatar.vue";
import KeyGuestReminder from "@/components/keys/guests/KeyGuestReminder.vue";
import { Getter } from "vuex-class";
import EditKeyAccess from "@/components/keys/EditKeyAccess.vue";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiEmailSyncOutline, mdiKey, mdiKeyChange, mdiPlus } from "@mdi/js";
import debounce from "debounce";
import SimpleDialog from "@/components/SimpleDialog.vue";
import KeyAddGuestDialog from "@/components/keys/guests/KeyAddGuestDialog.vue";
import { ROWS_PER_PAGE } from "@/utils/constants";

const septeoImage = require("@/assets/images/septeo.png");
export const DEBOUNCE_TIME_MILLI: number = 1000;

@Component({
  components: {
    KeyAddGuestDialog,
    SimpleDialog,
    EditKeyAccess,
    KeyGuestReminder,
    UserListAvatar,
    OverPriceSms,
    SvgIcon,
  },
})
export default class KeyGuestsTable extends Vue {

  get canEditGuests(): boolean {
    return this.canDoInEnvCompany("MANAGE_KEY_GUESTS");
  }

  get keyGuestsHeaders(): any[] {
    const headers: any[] = [{
      value: "name",
      text: this.userType === "user" ? this.$tc("key.userCount") : this.$t("user.groups"),
      sortable: false,
    }];
    if (this.canEditGuests) {
      headers.push({
        value: "action",
        sortable: false,
      });
    }
    return headers;
  }

  get keyAccessesHeaders(): any[] {
    const headers: any[] = [
      { value: "userGroupName", text: this.$t("KeyManagement.userOrGroup"), sortable: false },
      { value: "keyGroupName", text: this.$t("KeyManagement.keyOrGroup"), sortable: false },
      { value: "profile", text: this.$t("key.guest.profile"), sortable: false },
    ];
    if (this.keyAccesses.some((kA) => kA.startDate)) {
      headers.push({ value: "start", text: this.$t("common.start"), sortable: false });
    }
    if (this.keyAccesses.some((kA) => kA.endDate)) {
      headers.push({ value: "end", text: this.$t("common.end"), sortable: false });
    }
    headers.push(
      {
        value: "action",
        sortable: false,
        text: this.isMobile ? this.$tc("key.action") : null,
      },
    );
    if (this.keyAccesses.some((kA) => !!kA.septeoAgencyBookingId)) {
      headers.push({value: "septeo", sortable: false});
    }
    return headers;
  }

  @Prop({default: null})
  public targetId: number;

  @Prop({default: null})
  public targetType: "keys"|"keyGroups"|"nonTransferables";

  @Prop({default: null})
  public userType: "user"|"contacts";

  @Watch("search")
  public handlerSearch = debounce(this.fetchKeyGuests, DEBOUNCE_TIME_MILLI);

  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private envCompanyId: number|null;
  @Getter private envCompany: any;
  @Getter private isMobile: boolean;

  private keyGuests: any[] = [];
  private keyGuestsCount: number = 0;
  private loadingKeyGuests: boolean = false;
  private search: string = "";
  private pagination: any = {
    page: 1,
    itemsPerPage: 10,
  };
  private addGuestDialog: boolean = false;
  private detailedGuest: any = null;
  private loadingAddGuest: boolean = false;
  private keyAccessesDialog: boolean = false;
  private keyAccesses: any[] = [];
  private loadingGetKeyAccesses: boolean = false;
  private removeKeyAccessDialog: boolean = false;
  private removedKeyAccess: any = null;
  private loadingRemoveKeyAccess: boolean = false;

  private editKeyAccessDialog: boolean = false;
  private editKeyAccess: any = null;
  private keyGuestReminderDialog: boolean = false;
  private keyGuestReminderUsers: object[] = [];

  private icons = {
    mdiKey,
    mdiKeyChange,
    mdiPlus,
    mdiEmailSyncOutline,
  };
  private septeoImage = septeoImage;

  private ROWS_PER_PAGE = ROWS_PER_PAGE;

  @Watch("pagination", {deep: true})
  public handlerPagination(val, oldVal) {
    if (val.page !== oldVal.page || val.itemsPerPage !== oldVal.itemsPerPage) {
      this.fetchKeyGuests();
    }
  }

  private mounted() {
    this.reset();
  }

  private reset() {
    this.closeKeyAccessesDialog();
    this.closeKeyGuestReminder();
    this.closeEditKeyAccess();
    this.closeRemoveKeyAccessDialog();
    this.fetchKeyGuests();
  }

  private async fetchKeyGuests(): Promise<void> {
    this.loadingKeyGuests = true;
    const module = this.targetType;
    const action = this.userType === "user" ? "getGuestsSoloById" : "getGuestsGroupsById";
    const payload = {
      id: this.targetId,
      pagination: {
        ...this.pagination,
        search: this.search,
      },
    };
    return this.$store.dispatch(`${module}/${action}`, payload)
      .then((res) => {
        this.keyGuests = res?.rows || [];
        this.keyGuestsCount = res?.count || 0;
      })
      .finally(() => {
        this.loadingKeyGuests = false;
        this.$emit("loaded");
      });
  }

  private async resetKeyAccesses(): Promise<void> {
    this.closeEditKeyAccess();
    await this.getKeyAccesses();
  }

  private openEditKeyAccess(item: any): void {
    this.editKeyAccess = JSON.parse(JSON.stringify(item));
    this.editKeyAccessDialog = true;
  }

  private closeEditKeyAccess(): void {
    this.editKeyAccessDialog = false;
    this.editKeyAccess = null;
  }

  private openKeyGuestReminder(users?: any[]): void {
    this.loadingAddGuest = false;
    this.keyGuestReminderUsers = users;
    this.keyGuestReminderDialog = true;
  }

  private closeKeyGuestReminder(): void {
    this.keyGuestReminderDialog = false;
    this.addGuestDialog = false;
    this.keyGuestReminderUsers = [];
  }

  private openKeyAccessesDialog(entity): void {
    this.keyAccessesDialog = true;
    this.detailedGuest = entity;
    this.getKeyAccesses();
  }

  private async getKeyAccesses(): Promise<void> {
    this.loadingGetKeyAccesses = true;
    await this.$store.dispatch("keyAccesses/getByEntities", {
      targetId: this.targetId,
      targetType: this.targetType,
      userId: this.userType === "user" ? this.detailedGuest.id : undefined,
      userGroupId: this.userType === "contacts" ? this.detailedGuest.id : undefined,
    })
      .then((data) => {
        this.keyAccesses = data;
      })
      .catch((err) => {
        this.$store.commit("alerts/displayError", {
          msg: this.$t(`alerts.error.${err?.response?.data?.error || "default"}`),
        });
      })
      .finally(() => {
        this.loadingGetKeyAccesses = false;
      });
  }

  private closeKeyAccessesDialog(): void {
    this.keyAccessesDialog = false;
    this.keyAccesses = [];
  }

  private openRemoveKeyAccessDialog(item): void {
    this.removedKeyAccess = item;
    this.removeKeyAccessDialog = true;
  }

  private closeRemoveKeyAccessDialog(): void {
    this.removedKeyAccess = null;
    this.removeKeyAccessDialog = false;
  }

  private async removeKeyAccess(): Promise<void> {
    this.loadingRemoveKeyAccess = true;
    await this.$store.dispatch("keyAccesses/deleteById", {
      id: this.removedKeyAccess.id,
    }).then(async () => {
      this.closeRemoveKeyAccessDialog();
      this.fetchKeyGuests();
      await this.getKeyAccesses();
      if (this.keyAccesses.length === 0) {
        this.closeKeyAccessesDialog();
      }
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    }).finally(() => {
      this.loadingRemoveKeyAccess = false;
    });
  }

  private getUserOrGroupName = (userGroup): string => userGroup.name
    || userGroup.Users[0].displayName

  private getKeyOrGroupName = (keyGroup): string => keyGroup.name
    || keyGroup.Keys?.[0]?.name
    || keyGroup.NonTransferables?.[0]?.name

  private resetEvent(): void {
    this.$emit("reset");
    this.reset();
  }

  private clickRow(item) {
    const route = this.userType === "user" ? "user" : "user-group";
    return this.$router.push({name: route, params: {id: item.id}});
  }

}
